import { useState, useEffect, type FormEvent } from 'react';
import { api, setToken, ApiError } from '../api';
import { useApp } from '../store';
import { Avatar, Button, Input, Window, Spinner } from '../components/ui';
import { toast } from '../components/Toast';

type Mode = 'login' | 'register' | 'forgot' | '2fa';

export function AuthScreen({ invite }: { invite?: string } = {}) {
  const setUser = useApp((s) => s.setUser);

  const [mode, setMode] = useState<Mode>('login');
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(invite?.toUpperCase() ?? '');
  // Opened from an /invite/:code link: which server it is (public preview).
  const [invitePreview, setInvitePreview] = useState<{ name: string; memberCount: number; iconUrl: string | null } | null>(null);
  useEffect(() => {
    if (!invite) return;
    api.invitePreview(invite).then(setInvitePreview).catch(() => setInvitePreview(null));
  }, [invite]);
  // Two-factor step: the pending token from a correct password, the typed
  // code, and whether the user switched to a recovery code.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set once a password-reset email has been requested (forgot mode). The
  // response is intentionally the same whether or not the address exists.
  const [forgotSent, setForgotSent] = useState(false);

  // Public registration setting. Loaded once on mount. When false, the
  // signup form is replaced with a "registrations closed" message — but
  // users can still register via an invite code (entered in a field that
  // appears for that case).
  const [registrationEnabled, setRegistrationEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    api.authConfig()
      .then((cfg) => setRegistrationEnabled(cfg.registrationEnabled))
      .catch(() => setRegistrationEnabled(true)); // fail open — let backend reject if needed
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await api.forgotPassword(email);
        setForgotSent(true);
        return;
      }
      if (mode === '2fa' && mfaToken) {
        try {
          const done = await api.loginTwoFactor({ mfaToken, code });
          setToken(done.token);
          setUser(done.user);
          if (done.usedRecoveryCode) {
            toast.info('You used a recovery code. Each works once — make new ones in Settings → Account.');
          }
        } catch (err) {
          // The pending token lasts 5 minutes; after that, start over.
          if (err instanceof ApiError && (err.details as { mfaExpired?: boolean } | undefined)?.mfaExpired) {
            setMode('login');
            setMfaToken(null);
            setCode('');
          }
          throw err;
        }
        return;
      }
      const result =
        mode === 'login'
          ? await api.login({ identifier, password })
          : await api.register({
              username,
              email,
              password,
              // Only send invite code if there is one. Backend tolerates
              // it when registration is open, requires it when closed.
              ...(inviteCode ? { inviteCode: inviteCode.trim() } : {}),
            });
      if ('mfaRequired' in result) {
        setMfaToken(result.mfaToken);
        setCode('');
        setUseRecovery(false);
        setMode('2fa');
        return;
      }
      setToken(result.token);
      setUser(result.user);

      // If we registered with an invite code, auto-join the server now
      // that we have a token. Failures here are non-fatal — user can
      // join manually after.
      if (mode === 'register' && inviteCode) {
        try {
          await api.joinServer(inviteCode.trim());
        } catch {}
      }
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="h-full w-full flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative — subtle giant logo behind */}
      <div
        className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.04]"
        aria-hidden
      >
        <svg viewBox="0 0 100 100" width="800" height="800">
          <path
            d="M50 18c-4 0-7 3-7 7s3 7 7 7 7-3 7-7-3-7-7-7zm-9 22l-5 13 3 16h6l-2-10 4-5v22h6V61l4 5-2 10h6l3-16-5-13H41z"
            fill="currentColor"
          />
        </svg>
      </div>

      <div className="w-full max-w-[380px] animate-slide-up relative z-10">
        <Window
          title={
            <span className="flex items-center gap-2">
              <span className="inline-block w-5 h-5 rounded-full bg-accent-text/20" style={{
                background: 'radial-gradient(circle at 30% 30%, #fff, transparent 60%), #1A1612',
              }} />
              {mode === 'login' || mode === '2fa' ? 'Sign On' : mode === 'forgot' ? 'Reset Password' : 'Create Account'}
            </span>
          }
        >
          <div className="p-5 bg-surface space-y-4">
            <div className="text-center pb-2">
              <div className="font-display text-2xl font-bold tracking-tight text-text">
                doof.lol
              </div>
              <div className="text-xs text-text-muted mt-0.5">
                {mode === 'login'
                  ? "Welcome back, let's get you online."
                  : mode === '2fa'
                  ? useRecovery
                    ? 'Enter one of the recovery codes you saved.'
                    : 'Enter the 6-digit code from your authenticator app.'
                  : mode === 'forgot'
                  ? "Enter your email and we'll send a reset link."
                  : registrationEnabled === false
                    ? 'Public sign-ups are closed. Got an invite code?'
                    : 'Pick a screen name and join the chat.'}
              </div>
            </div>

            {invitePreview && mode !== '2fa' && (
              <div className="flex items-center gap-3 rounded-xl border border-accent/40 bg-accent/10 p-2.5">
                <Avatar name={invitePreview.name} url={invitePreview.iconUrl} size={36} />
                <div className="min-w-0 text-xs text-text">
                  You've been invited to <strong className="wrap-anywhere">{invitePreview.name}</strong>
                  <div className="text-text-muted">
                    {mode === 'register'
                      ? "Create an account and you'll join automatically."
                      : 'Sign in to join, or create an account below.'}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-3">
              {mode === '2fa' ? (
                <Input
                  key={useRecovery ? 'recovery' : 'totp'}
                  label={useRecovery ? 'Recovery code' : 'Authentication code'}
                  name="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  {...(useRecovery
                    ? { placeholder: 'xxxx-xxxx', autoComplete: 'off', maxLength: 16 }
                    : { inputMode: 'numeric' as const, autoComplete: 'one-time-code', pattern: '[0-9 ]{6,7}', maxLength: 7, placeholder: '123456' })}
                  required
                  autoFocus
                />
              ) : mode === 'forgot' ? (
                forgotSent ? (
                  <div className="rounded-xl border border-border-soft p-2.5 bg-elevated/40 text-text-muted text-xs">
                    If an account exists for that email, a password-reset link is
                    on its way. Check your inbox (and spam folder).
                  </div>
                ) : (
                  <Input
                    label="Email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    autoFocus
                  />
                )
              ) : mode === 'login' ? (
                <>
                  <Input
                    label="Screen Name or Email"
                    name="identifier"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    autoComplete="username"
                    required
                    autoFocus
                  />
                  <Input
                    label="Password"
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </>
              ) : (
                <>
                  <Input
                    label="Screen Name"
                    name="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    pattern="[a-zA-Z0-9_.\-]{2,32}"
                    title="2-32 characters: letters, numbers, dots, dashes, underscores"
                    autoComplete="username"
                    required
                    autoFocus
                  />
                  <Input
                    label="Email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                  <Input
                    label="Password"
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    autoComplete="new-password"
                    required
                  />
                  <Input
                    label={registrationEnabled === false ? 'Invite Code (required)' : 'Invite Code (optional)'}
                    name="inviteCode"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder={registrationEnabled === false ? 'You need an invite to sign up' : 'Have one? Auto-joins that server'}
                    maxLength={32}
                    required={registrationEnabled === false}
                  />
                </>
              )}

              {error && (
                <div className="rounded-xl border border-danger/30 p-2.5 bg-danger/10 text-danger text-xs font-medium">
                  {error}
                </div>
              )}

              {!(mode === 'forgot' && forgotSent) && (
                <Button type="submit" variant="primary" fullWidth disabled={busy}>
                  {busy ? (
                    <Spinner />
                  ) : mode === 'login' ? (
                    'Sign On'
                  ) : mode === '2fa' ? (
                    'Verify'
                  ) : mode === 'forgot' ? (
                    'Send reset link'
                  ) : (
                    'Create Account'
                  )}
                </Button>
              )}

              {mode === '2fa' && (
                <div className="text-center">
                  <button
                    type="button"
                    className="aim-link text-xs"
                    onClick={() => { setUseRecovery((r) => !r); setCode(''); setError(null); }}
                  >
                    {useRecovery ? 'Use your authenticator app instead' : 'Lost your phone? Use a recovery code'}
                  </button>
                </div>
              )}

              {mode === 'login' && (
                <div className="text-center">
                  <span
                    className="aim-link text-xs"
                    onClick={() => { setMode('forgot'); setError(null); setForgotSent(false); }}
                  >
                    Forgot your password?
                  </span>
                </div>
              )}
            </form>

            <div className="border-t border-border-soft pt-3 text-center text-xs text-text-muted">
              {mode === 'forgot' || mode === '2fa' ? (
                <span
                  className="aim-link"
                  onClick={() => { setMode('login'); setError(null); setForgotSent(false); setMfaToken(null); setCode(''); }}
                >
                  Back to sign in
                </span>
              ) : mode === 'login' ? (
                <>
                  {registrationEnabled === false ? 'Have an invite code? ' : "Don't have an account yet? "}
                  <span className="aim-link" onClick={() => { setMode('register'); setError(null); }}>
                    Sign up here
                  </span>
                </>
              ) : (
                <>
                  Already have one?{' '}
                  <span className="aim-link" onClick={() => { setMode('login'); setError(null); }}>
                    Sign in
                  </span>
                </>
              )}
            </div>
          </div>
        </Window>

        <div className="text-center text-[11px] text-text-subtle mt-3 italic">
          a little corner of the internet for friends
        </div>
      </div>
    </div>
  );
}
